import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Config } from '../config/config';

export interface ResultCompareJobSummary {
  jobId: string;
  batchId: string;
  tableId: string;
  fileId: string;
  fileName?: string;
  createdAt: number;
  finishTime?: string;
  schemaVersion?: number;
  status?: string;
  timeSeconds?: number;
  hasResult?: boolean;
}

export interface ResultCompareListJobsResponse {
  jobs: ResultCompareJobSummary[];
  total: number;
}

export interface ResultCompareBatchSummary {
  batchId: string;
  tableId: string;
  createdAt: number;
  finishTime?: string;
  schemaVersion?: number;
  versionedSchemaId?: string;
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  status: string;
  fileIds: string[];
}

export interface ResultCompareListBatchesResponse {
  batches: ResultCompareBatchSummary[];
  total: number;
}

export interface ResultCompareComparisonEntry {
  path?: string;
  level?: 'PASS' | 'WARN' | 'FAIL';
  value?: unknown;
  tags?: string[];
  metrics?: {
    presence?: number;
    consistency?: number;
    confidence?: number;
  };
  evaluation?: {
    values?: unknown[];
    reason?: string;
    similarity?: 'EQUAL' | 'SIMILAR' | 'DIFFERENT';
  };
}

export interface ResultCompareConsistency {
  overall_percentage?: number;
  level?: string;
  quality?: number;
  total_paths?: number;
  penalty_breakdown?: {
    all_match?: number;
    value_different?: number;
    missing_key?: number;
  };
}

export interface ResultCompareComparisonResult {
  consistency?: ResultCompareConsistency;
  comparisons?: ResultCompareComparisonEntry[];
}

export interface ResultCompareEnrichedResponse {
  comparison?: ResultCompareComparisonResult;
  batches?: Array<{
    batchId: string;
    createdAt: number;
    schemaVersion?: number;
    totalFiles: number;
    completedFiles: number;
    failedFiles: number;
    status: string;
  }>;
  jobs?: ResultCompareJobSummary[];
  tableId?: string;
  comparedAt?: number;
}

export interface CompareByBatchIdsRequest {
  batchIds: string[];
  fileId?: string;
}

export interface CompareTableVersionsRequest {
  tableId: string;
  schemaVersions?: number[];
  lastNBatches?: number;
  fileName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ResultCompareService {
  private apiUrl = `${Config.backend_url}/result-compare`;

  constructor(private http: HttpClient) {}

  getTableJobs(
    tableId: string,
    limit: number = 200,
    offset: number = 0
  ): Observable<ResultCompareListJobsResponse> {
    const params = new HttpParams()
      .set('limit', limit)
      .set('offset', offset);
    return this.http.get<ResultCompareListJobsResponse>(
      `${this.apiUrl}/tables/${tableId}/jobs`,
      { params }
    );
  }

  compareByJobs(jobIds: string[]): Observable<ResultCompareEnrichedResponse> {
    return this.http.post<ResultCompareEnrichedResponse>(
      `${this.apiUrl}/by-jobs`,
      { jobIds }
    );
  }

  getTableBatches(
    tableId: string,
    limit: number = 50,
    offset: number = 0
  ): Observable<ResultCompareListBatchesResponse> {
    const params = new HttpParams()
      .set('limit', limit)
      .set('offset', offset);
    return this.http.get<ResultCompareListBatchesResponse>(
      `${this.apiUrl}/tables/${tableId}/batches`,
      { params }
    );
  }

  compareByBatches(
    batchIds: string[],
    fileId?: string
  ): Observable<ResultCompareEnrichedResponse> {
    const body: CompareByBatchIdsRequest = { batchIds };
    if (fileId != null) {
      body.fileId = fileId;
    }
    return this.http.post<ResultCompareEnrichedResponse>(
      `${this.apiUrl}/by-batches`,
      body
    );
  }

  compareTableVersions(
    body: CompareTableVersionsRequest
  ): Observable<ResultCompareEnrichedResponse> {
    return this.http.post<ResultCompareEnrichedResponse>(
      `${this.apiUrl}/table-versions`,
      body
    );
  }
}
